import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import FormSection from 'components/sales/formSection/formSectionNew';
import CardSection from 'components/sales/cardSection/cardSection';
import DropDownInput from 'components/sales/Input/dropDownInput/dropDownInput';
import ConstantInput from 'components/sales/Input/constantInput/constantInput';
import TextAreaInput from 'components/sales/Input/textAreaInput/textAreaInput';
import DatePicker from 'components/sales/datePicker/datePicker';
import DocumentUploadButton from 'services/utils/fileUpload/DocumentUploadButton';
import { IMAGE_VALIDATION_KEYS } from 'services/utils/fileUpload/imageValidation';
import { Button } from 'react-bootstrap';
import localization from 'assets/localization/localization';
import {
  emptyImageItem,
  displayDateToJsDate,
  isEndBeforeStart,
  readProjectIdsFromCsvFile,
  formatTargetIdsText,
  parseTargetIds,
} from './dynamicContentUtils';
import './dynamicContentsForm.css';

const ruleTypeOptions = [
  { id: 'all', name: 'All' },
  { id: 'include', name: 'Include' },
  { id: 'exclude', name: 'Exclude' },
];

const UPLOAD_OPTIONS = {
  service_name: 'dynamiccontents',
  doc_type: 'image',
  is_public: 'true',
};

const DynamicContentsFormFields = ({
  formData,
  setFormData,
  onSubmit,
  isLoading,
  isEdit = false,
}) => {
  const csvInputRef = useRef(null);
  const showTargetIds =
    formData.rule_type === 'include' || formData.rule_type === 'exclude';

  const projectIdCount = useMemo(() => {
    if (!showTargetIds) return 0;
    return parseTargetIds(formData.target_ids_text).length;
  }, [showTargetIds, formData.target_ids_text]);

  const imageItems =
    formData.image_items?.length ? formData.image_items : [emptyImageItem()];

  const handleField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStartDateChange = (val) => {
    setFormData((prev) => {
      const next = { ...prev, start_at_display: val };
      if (val && prev.end_at_display && isEndBeforeStart(val, prev.end_at_display)) {
        next.end_at_display = null;
      }
      return next;
    });
  };

  const endMinDate = displayDateToJsDate(formData.start_at_display);

  const startMinDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const handleCsvFileChange = async (file) => {
    if (!file) return;

    try {
      const ids = await readProjectIdsFromCsvFile(file);

      setFormData((prev) => ({
        ...prev,
        target_ids_text: formatTargetIdsText(ids),
      }));
      toast.success(
        `${localization.dynamicContentCsvImported} (${ids.length} ID${ids.length === 1 ? '' : 's'})`
      );
    } catch (err) {
      toast.error(err?.message || 'Failed to parse CSV.');
    } finally {
      if (csvInputRef.current) {
        csvInputRef.current.value = '';
      }
    }
  };

  const updateImageItem = (index, field, value) => {
    setFormData((prev) => {
      const next = [...(prev.image_items?.length ? prev.image_items : [emptyImageItem()])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, image_items: next };
    });
  };

  const addImageSlot = () => {
    setFormData((prev) => ({
      ...prev,
      image_items: [...(prev.image_items?.length ? prev.image_items : [emptyImageItem()]), emptyImageItem()],
    }));
  };

  const removeImageSlot = (index) => {
    setFormData((prev) => {
      const next = [...(prev.image_items?.length ? prev.image_items : [emptyImageItem()])];
      next.splice(index, 1);
      return { ...prev, image_items: next.length ? next : [emptyImageItem()] };
    });
  };

  return (
    <FormSection>
      <CardSection title={localization.dynamicContentDetails}>
        <div className="row">
          <div className="col-md-6">
            <ConstantInput
              label={localization.title}
              value={formData.title}
              handleChange={(val) => handleField('title', val)}
              isStarRequired
              required
            />
          </div>
          <div className="col-md-6">
            <DropDownInput
              label={localization.ruleType}
              data={ruleTypeOptions}
              getValueById={formData.rule_type}
              getIdValue={(val) => handleField('rule_type', val?.id ?? val)}
              isStarRequired
              required
            />
          </div>
          {showTargetIds && (
            <div className="col-md-12 dynamic-contents-target-ids-block">
              <label className="dynamic-contents-target-ids-section-label">
                {localization.targetIds}
                <span className="text-danger"> *</span>
              </label>
              <p className="dynamic-contents-target-ids-section-hint text-muted small">
                {localization.dynamicContentProjectIdsEitherHint}
              </p>

              <div className="dynamic-contents-target-ids-options">
                <div className="dynamic-contents-target-ids-option dynamic-contents-target-ids-option-csv">
                  <div className="dynamic-contents-target-ids-option-title">
                    {localization.dynamicContentUploadCsvOption}
                  </div>
                  <p className="dynamic-contents-target-ids-option-hint text-muted small">
                    {localization.dynamicContentUploadCsvOptionHint}
                  </p>
                  <label className="btn btn-outline-secondary btn-sm dynamic-contents-csv-upload-btn">
                    {localization.uploadCSV}
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      className="d-none"
                      onChange={(e) => handleCsvFileChange(e.target.files?.[0])}
                    />
                  </label>
                </div>

                <div className="dynamic-contents-target-ids-or" aria-hidden="true">
                  <span>{localization.orLabel}</span>
                </div>

                <div className="dynamic-contents-target-ids-option dynamic-contents-target-ids-option-text">
                  <TextAreaInput
                    type="text"
                    rows={6}
                    className="dynamic-contents-target-ids-textarea"
                    label={localization.dynamicContentEnterProjectIdsManually}
                    value={formData.target_ids_text}
                    inputValue={formData.target_ids_text}
                    handleChange={(e) => handleField('target_ids_text', e.target.value)}
                  />
                  <p className="dynamic-contents-target-ids-option-hint text-muted small mb-0">
                    {localization.dynamicContentPasteIdsHint}
                  </p>
                </div>
              </div>

              {projectIdCount > 0 ? (
                <p className="dynamic-contents-project-id-count text-muted small mb-0">
                  {localization.dynamicContentProjectIdCount.replace(
                    '%s',
                    projectIdCount.toLocaleString()
                  )}
                </p>
              ) : null}
            </div>
          )}
          <div className="col-md-12 dynamic-contents-images-section">
            <label className="dynamic-contents-images-label">
              {localization.images}
              <span className="text-danger"> *</span>
            </label>
            <p className="dynamic-contents-images-hint">
              {localization.dynamicContentImageDimensionRequirement}
            </p>
            {imageItems.map((item, index) => (
              <div
                key={`dynamic-content-image-${index}`}
                className="dynamic-content-image-slot"
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small">
                    {localization.image} {index + 1}
                  </span>
                  {imageItems.length > 1 && (
                    <Button
                      type="button"
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeImageSlot(index)}
                    >
                      {localization.remove}
                    </Button>
                  )}
                </div>
                <DocumentUploadButton
                  value={item.image_url}
                  onUpload={(url) => updateImageItem(index, 'image_url', url)}
                  accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png"
                  uploadOptions={UPLOAD_OPTIONS}
                  validationKey={IMAGE_VALIDATION_KEYS.DYNAMIC_CONTENT_BANNER}
                  buttonLabel={localization.uploadBannerImage}
                  uploadedLabel="✓ Image uploaded — click to change"
                  required={index === 0}
                />
                {item.image_url ? (
                  <a
                    href={item.image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="small d-inline-block mt-2"
                  >
                    {localization.viewUploadedImage}
                  </a>
                ) : null}
                <div className="mt-3">
                  <ConstantInput
                    label={localization.redirectUrl}
                    value={item.redirect_url}
                    handleChange={(val) => updateImageItem(index, 'redirect_url', val)}
                  />
                  <p className="text-muted small mb-0 mt-1">
                    {localization.dynamicContentRedirectUrlHint}
                  </p>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              className="dynamic-contents-add-image-btn"
              onClick={addImageSlot}
            >
              + {localization.addImage}
            </Button>
          </div>
        </div>
      </CardSection>

      <CardSection title={localization.dynamicContentSchedule} panelN="">
        <div className="dynamic-contents-schedule-dates">
          <div className="row">
            <div className="col-md-6">
              <DatePicker
                label={localization.startDate}
                value={formData.start_at_display}
                handleChange={handleStartDateChange}
                minimumDate={startMinDate}
                required
                requiredMessage={localization.dynamicContentStartDateRequired}
              />
            </div>
            <div className="col-md-6">
              <DatePicker
                label={localization.endDate}
                value={formData.end_at_display}
                handleChange={(val) => handleField('end_at_display', val)}
                minimumDate={endMinDate}
                required={Boolean(formData.start_at_display)}
                disabled={!formData.start_at_display}
                requiredMessage={localization.dynamicContentEndDateRequired}
              />
            </div>
          </div>
        </div>
      </CardSection>

      <div className="text-right mb-4">
        <Button className="btn btn-primary" onClick={onSubmit} disabled={isLoading}>
          {localization.submit}
        </Button>
      </div>
    </FormSection>
  );
};

DynamicContentsFormFields.defaultProps = {
  isLoading: false,
  isEdit: false,
};

DynamicContentsFormFields.propTypes = {
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isEdit: PropTypes.bool,
};

export default DynamicContentsFormFields;
